import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import { Layout, theme, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import { reportingOp } from 'store/ducks/Reporting';
import { RootState } from 'store/configureStore';
import {
  IFilters,
  TitleListEN,
  TitleListsInternalControls
} from 'store/ducks/globalTypes';
import { TitleKeyInternalControls } from 'store/ducks/Management/types';
import { IInternalControlLayoutProps } from 'store/ducks/InternalControls/types';
import { PROCESS_NAVIGATION_BUTTONS } from 'constants/routes';
import { pagesWithUnsavedChanges } from '../../../constants/unsavedChangesPages';
import CustomSelect from 'components/CustomSelect';
import { internalControlsOp } from 'store/ducks/InternalControls';
import ApiService from 'utils/ApiCalls';
import * as Styled from 'components/Layouts/Layout.styled';

const { Sider } = Layout;
const { useToken } = theme;
const { Content } = Layout;
const FiltersParam = withDefault(ArrayParam, []);

const InternalControlLayout: React.FC<IInternalControlLayoutProps> = ({
  activeTab,
  activeKey,
  children,
  onSave,
  additionalFilters
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useToken();
  const { t } = useTranslation();

  const [dndFilterOptions, setDndFilterOptions] = useState<
    Record<string, IFilters['options']>
  >({});
  const [controlsFilterOptions, setControlsFilterOptions] = useState<
    Record<string, IFilters['options']>
  >({});
  const [mainOrganizations, setMainOrganizations] = useState<any[]>([]);
  const [bruAssignedOptions, setBruAssignedOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const controlsItems = [
    { key: 'control_owner', label: t('CONTROL_OWNER') }
  ] as const;

  const bruAssignedPages = [
    'risks',
    'controls',
    'controlsTesting',
    'controlsTestingDesignEffectiveness',
    'controlAttestation'
  ];

  const page = location.pathname.split('/').pop();

  const [query, setQuery] = useQueryParams({
    industry: FiltersParam,
    module: FiltersParam,
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam,
    control_owner: FiltersParam,
    main_organization: FiltersParam,
    bru_assigned: FiltersParam
  });

  const handleDndFilterChange = (value: string | string[], item: string) => {
    const valueArray: string[] = Array.isArray(value)
      ? value.map(String)
      : value
      ? [String(value)]
      : [];

    const key = item.toLowerCase().replace(/ /g, '_') as keyof typeof query;

    setQuery((latestQuery: any) => ({
      ...latestQuery,
      [key]: valueArray
    }));
  };

  const { lang, scheme, saveDisabled, hasChanges } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      scheme: state.global.scheme,
      saveDisabled: state.internalControls.saveDisabled,
      hasChanges: state.internalControls.hasChanges
    })
  );

  const shouldCheckUnsavedChanges = pagesWithUnsavedChanges.includes(
    page || ''
  );

  // Centralized unsaved changes confirmation
  const showUnsavedChangesConfirmation = (onConfirm: () => void) => {
    Modal.confirm({
      title: t('UNSAVED_CHANGES_TITLE'),
      content: t('UNSAVED_CHANGES_CONTENT'),
      okText: t('YES'),
      cancelText: t('NO'),
      okType: 'danger',
      onOk: onConfirm
    });
  };

  // Set up global unsaved changes detection for GoBackButton
  useEffect(() => {
    window.hasUnsavedChanges = shouldCheckUnsavedChanges && hasChanges;

    if (shouldCheckUnsavedChanges) {
      window.showUnsavedChangesModal = () => {
        return new Promise((resolve) => {
          showUnsavedChangesConfirmation(() => resolve(true));
        });
      };
    }

    return () => {
      window.hasUnsavedChanges = false;
      window.showUnsavedChangesModal = undefined;
    };
  }, [page, hasChanges, shouldCheckUnsavedChanges]);

  const applyFilters = () => {
    if (shouldCheckUnsavedChanges && hasChanges) {
      showUnsavedChangesConfirmation(() => {
        dispatch(internalControlsOp.setDrawTable(true));
      });
    } else {
      dispatch(internalControlsOp.setDrawTable(true));
    }
  };
  useEffect(() => {
    const fetchMainOrganizations = async () => {
      try {
        const res = await ApiService.getMainOrganizations();

        setMainOrganizations(res.data.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchMainOrganizations();
  }, []);

  useEffect(() => {
    const fetchBruAssigned = async () => {
      try {
        const res = await ApiService.getBruAssigned();
        setBruAssignedOptions(
          res.data.data.map((b: { value: string; label: string }) => ({
            label: b.label,
            value: b.value
          }))
        );
      } catch (error) {
        console.error('Failed to fetch BRU Assigned options', error);
      }
    };

    if (bruAssignedPages.includes(page || '')) {
      fetchBruAssigned();
    }
  }, [page, lang]);

  useEffect(() => {
    dispatch(reportingOp.setFilters(lang));
  }, []);

  useEffect(() => {
    if (
      page === 'dnd' ||
      page === 'risks' ||
      page === 'controls' ||
      page === 'controlsTesting' ||
      page === 'controlsTestingDesignEffectiveness' ||
      page === 'controlAttestation'
    ) {
      const fetchDndFilters = async () => {
        try {
          const [
            groupRes,
            divisionRes,
            regionRes,
            legalEntityRes,
            businessUnitRes
          ] = await Promise.all([
            ApiService.getGroupBPR(),
            ApiService.getDivisionBPR(),
            ApiService.getRegionBPR(),
            ApiService.getLegalEntitiesBPR(),
            ApiService.getBusinessUnitBPR()
          ]);

          const normalizeOptions = (arr: any[]) =>
            arr.map((item) => ({ label: item.name, value: item.id }));

          setDndFilterOptions({
            Group: normalizeOptions(groupRes?.data?.data),
            Division: normalizeOptions(divisionRes?.data?.data),
            Region: normalizeOptions(regionRes?.data?.data),
            'Legal Entity': normalizeOptions(legalEntityRes?.data?.data),
            'Business Unit': normalizeOptions(businessUnitRes?.data?.data)
          });
        } catch (error) {
          console.error('Failed to fetch DnD filter options', error);
        }
      };

      fetchDndFilters();
    }
  }, [page, lang]);

  useEffect(() => {
    if (
      page === 'controls' ||
      page === 'controlsTesting' ||
      page === 'controlsTestingDesignEffectiveness' ||
      page === 'controlAttestation'
    ) {
      const fetchControlsFilters = async () => {
        try {
          const [controlOwners] = await Promise.all([
            ApiService.getOwnersBPR()
          ]);
          const normalizeOptions = (arr: any[]) =>
            arr.map((item) => ({
              label: item.name,
              value: item.id
            }));

          setControlsFilterOptions({
            control_owner: normalizeOptions(controlOwners?.data?.data)
          });
        } catch (error) {
          console.error('Failed to fetch Controls filter options', error);
        }
      };

      fetchControlsFilters();
    }
  }, [page, lang]);

  useEffect(() => {
    dispatch(reportingOp.setDrawTable(false));
  }, [activeTab, activeKey]);

  const titleKeyInternalControls: TitleKeyInternalControls = (() => {
    switch (page) {
      case 'controls':
        return 'CONTROL_REGISTER';
      case 'controlsTesting':
        return 'CONTROLS_TEST';
      case 'controlsTestingDesignEffectiveness':
        return 'CONTROLS_TEST_DESIGN';
      case 'controlAttestation':
        return 'CONTROL_ATTESTATION';
      case 'risks':
        return 'RISK_REGISTER';
      case 'businessProcesses':
        return 'BUSINESS_PROCESSES';
      case 'businessProcessesL1':
        return 'BUSINESS_PROCESSESL1';
      case 'businessProcessesL2':
        return 'BUSINESS_PROCESSESL2';
      case 'businessProcessesL3':
        return 'BUSINESS_PROCESSESL3';
      case 'dnd':
        return 'BUSINESS_PROCESS_MAPPING';
      case 'allocation':
        return 'ALLOCATION';
      case 'processAllocation':
        return 'PROCESS_ALLOCATION';
      default:
        return 'CONTROL_REGISTER';
    }
  })();

  const getGoBackUrl = () => {
    if (
      page === 'controlsTesting' ||
      page === 'controlsTestingDesignEffectiveness' ||
      page === 'controlAttestation'
    ) {
      return '/risk-controls/assess_and_test';
    } else if (page === 'allocation' || page === 'processAllocation') {
      return '/risk-controls/allocation';
    } else if (page === 'dnd') {
      return '/internal_controls';
    }
    return '/risk-controls/inventories';
  };

  const titles = TitleListsInternalControls[lang] || TitleListEN;

  const handleNavigation = (path: string) => {
    if (shouldCheckUnsavedChanges && hasChanges) {
      showUnsavedChangesConfirmation(() => {
        navigate(path);
      });
    } else {
      navigate(path);
    }
  };

  const getNavigationButtons = () => {
    const pageToExcludeMap = {
      businessProcessesL1: 'PROCESS_L1',
      businessProcessesL2: 'PROCESS_L2',
      businessProcessesL3: 'PROCESS_L3',
      businessProcesses: 'PROCESS_L4'
    };

    const excludeKey = pageToExcludeMap[page as keyof typeof pageToExcludeMap];
    return PROCESS_NAVIGATION_BUTTONS.filter(
      (btn) => btn.labelKey !== excludeKey
    );
  };

  const showNavigationButtons = [
    'businessProcesses',
    'businessProcessesL1',
    'businessProcessesL2',
    'businessProcessesL3'
  ].includes(page || '');

  const shouldShowFilters =
    location.pathname !== '/controlsAndRiskAllocation/allocation';

  const dndItems = [
    { label: t('GROUP'), key: 'Group' },
    { label: t('DIVISION'), key: 'Division' },
    { label: t('REGION'), key: 'Region' },
    { label: t('LEGALENTITY'), key: 'Legal Entity' },
    { label: t('BUSINESSUNIT'), key: 'Business Unit' }
  ] as const;

  const activeFilterKey = [
    ...dndItems.map((i) => i.key),
    'Main Organization'
  ].find((item) => {
    const key = item.toLowerCase().replace(/ /g, '_') as keyof typeof query;

    return Array.isArray(query[key]) && query[key].length > 0;
  });

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={titles[titleKeyInternalControls]}
        goBackButtons={
          <>
            <GoBackButton
              to={getGoBackUrl()}
              text={t('GO_BACK')}
              checkUnsavedChanges={shouldCheckUnsavedChanges}
            />
            <GoBackButton
              to='/working_area'
              text={t('GO_TO_WORKING_AREAS')}
              checkUnsavedChanges={shouldCheckUnsavedChanges}
            />
          </>
        }
      />

      <Styled.HorizontalFilterBar token={token} scheme={scheme}>
        {shouldShowFilters && (
          <Styled.FilteredContainerRisks
            isBusinessProcesses={page === 'businessProcesses'}
            isControlsPage={page === 'controls'}
          >
            {(page === 'dnd' ||
              page === 'risks' ||
              page === 'controls' ||
              page === 'controlsTestingDesignEffectiveness' ||
              page === 'controlAttestation' ||
              page === 'controlsTesting') &&
              dndItems.map(({ label, key: item }) => {
                const dndKey = item
                  .toLowerCase()
                  .replace(/ /g, '_') as keyof typeof query;

                const isActive = activeFilterKey === item;
                const shouldDisable = Boolean(activeFilterKey && !isActive);

                return (
                  <CustomSelect
                    key={item}
                    placeholder={label}
                    options={(dndFilterOptions[item] ?? []).map((opt) => ({
                      label: opt.label,
                      value: String(opt.value)
                    }))}
                    filters
                    allowClear
                    disabled={shouldDisable}
                    value={((query[dndKey] as number[]) || []).map(String)}
                    onChange={(value: string | string[]) => {
                      const valueArray: number[] = Array.isArray(value)
                        ? value.map(Number)
                        : value
                        ? [Number(value)]
                        : [];

                      handleDndFilterChange(
                        valueArray as unknown as string[],
                        item
                      );
                    }}
                  />
                );
              })}
            {(page === 'risks' || page === 'controls') && (
              <CustomSelect
                placeholder={t('MAIN_ORGANIZATIONS')}
                filters
                allowClear
                disabled={
                  Boolean(activeFilterKey) &&
                  activeFilterKey !== 'Main Organization'
                }
                options={mainOrganizations.map((org) => ({
                  label: org.name,
                  value: String(org.id)
                }))}
                value={(query.main_organization as string[]) || []}
                onChange={(value: string | string[]) => {
                  const valueArray = Array.isArray(value)
                    ? value
                    : value
                    ? [value]
                    : [];

                  setQuery((latestQuery: any) => ({
                    ...latestQuery,
                    main_organization: valueArray
                  }));
                }}
              />
            )}
            {bruAssignedPages.includes(page || '') && (
              <CustomSelect
                placeholder={t('BRU Assigned')}
                filters
                allowClear
                options={bruAssignedOptions}
                value={(query.bru_assigned as string[]) || []}
                onChange={(value: string | string[]) => {
                  const valueArray = Array.isArray(value)
                    ? value
                    : value
                    ? [value]
                    : [];

                  setQuery((latestQuery: any) => ({
                    ...latestQuery,
                    bru_assigned: valueArray
                  }));
                }}
              />
            )}
            {(page === 'controls' ||
              page === 'controlsTesting' ||
              page === 'controlsTestingDesignEffectiveness') &&
              controlsItems.map(({ key, label }) => (
                <CustomSelect
                  key={key}
                  placeholder={label}
                  options={(controlsFilterOptions[key] ?? []).map((opt) => ({
                    label: opt.label,
                    value: String(opt.value)
                  }))}
                  filters
                  allowClear
                  value={(query[key] as string[]) || []}
                  onChange={(value: string | string[]) => {
                    const valueArray = Array.isArray(value)
                      ? value
                      : value
                      ? [value]
                      : [];

                    handleDndFilterChange(valueArray, label);
                  }}
                />
              ))}
            {(page === 'businessProcesses' || page === 'dnd') &&
              additionalFilters && (
                <>
                  <Styled.Spacer />
                  {additionalFilters}
                </>
              )}

            {page !== 'processAllocation' && (
              <>
                <Styled.Spacer />
                <Styled.StyledButton
                  type='primary'
                  onClick={applyFilters}
                  className='display-save-button'
                >
                  {t('DISPLAY')}
                </Styled.StyledButton>
                <Styled.Spacer />
                <Styled.StyledButton
                  type='primary'
                  onClick={() => onSave?.(query)}
                  className='display-save-button'
                  disabled={saveDisabled}
                >
                  {t('SAVE')}
                </Styled.StyledButton>
              </>
            )}
          </Styled.FilteredContainerRisks>
        )}
        {showNavigationButtons && (
          <Styled.NavigationContainer
            isProcessL4={page === 'businessProcesses' || page === 'dnd'}
          >
            <Styled.NavigationSpace
              wrap
              isProcessL4={page === 'businessProcesses' || page === 'dnd'}
            >
              {getNavigationButtons().map((btn) => (
                <Styled.NavigationButton
                  key={btn.path}
                  type='default'
                  size='small'
                  onClick={() => handleNavigation(btn.path)}
                  token={token}
                  scheme={scheme}
                >
                  {t(btn.labelKey)}
                </Styled.NavigationButton>
              ))}
            </Styled.NavigationSpace>
          </Styled.NavigationContainer>
        )}
      </Styled.HorizontalFilterBar>
      <Styled.StyledLayout token={token}>
        <Layout>
          <Content>{children}</Content>
        </Layout>
        <Sider></Sider>
      </Styled.StyledLayout>
    </Styled.StyledLayout>
  );
};

export default InternalControlLayout;
